/**
 * Shared SkillsMatrix component — used by both the profile page (UserDrawer)
 * and the Technicians & Skills page. Skills grouped by board/category with
 * expandable/collapsible groups + set-all per group.
 */
import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { apiPatch } from '../hooks/useApi.js';

export interface SkillEntry {
  skillId: string;
  skillName: string;
  category: string;
  level: number;
  source: string;
  inferred?: boolean;
  confidence?: number;
}

const SKILL_LEVELS = ['None', 'Aware', 'Capable', 'Proficient', 'Expert'];
const LEVEL_COLORS: Record<number, string> = {
  0: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  1: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  2: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  3: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  4: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
};

export function SkillsMatrix({
  skills,
  canEdit,
  onSetLevel,
  refetch,
}: {
  skills: SkillEntry[];
  canEdit: boolean;
  onSetLevel?: (skillId: string, level: number) => Promise<void>;
  refetch?: () => void;
}) {
  const [collapsed, setCollapsed] = React.useState<Set<string>>(new Set());
  const [bulkLevel, setBulkLevel] = React.useState<Record<string, string>>({});

  function toggleGroup(category: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }

  async function setLevel(skillId: string, level: number) {
    if (onSetLevel) {
      await onSetLevel(skillId, level);
    } else if (refetch) {
      try {
        await apiPatch('/api/v1/skills/' + skillId, { level });
        refetch();
      } catch (e) { alert(e instanceof Error ? e.message : 'Failed'); }
    }
  }

  async function setAllForGroup(groupSkills: SkillEntry[], level: number) {
    if (onSetLevel) {
      await Promise.all(groupSkills.map((s) => onSetLevel(s.skillId, level)));
    } else if (refetch) {
      try {
        await Promise.all(groupSkills.map((s) => apiPatch('/api/v1/skills/' + s.skillId, { level })));
        refetch();
      } catch (e) { alert(e instanceof Error ? e.message : 'Failed'); }
    }
  }

  const groups = React.useMemo(() => {
    const map = new Map<string, SkillEntry[]>();
    for (const s of skills) {
      const cat = s.category || 'Uncategorized';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(s);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [skills]);

  if (skills.length === 0) {
    return <p className="text-sm text-[hsl(var(--content-subtle))] text-center py-4">No skills assigned.</p>;
  }

  return (
    <div className="space-y-3">
      {groups.map(([category, groupSkills]) => {
        const isCollapsed = collapsed.has(category);
        const expertCount = groupSkills.filter((s) => s.level >= 4).length;
        const assignedCount = groupSkills.filter((s) => s.level > 0).length;
        return (
          <div key={category} className="rounded-xl border border-[hsl(var(--border))] overflow-hidden">
            {/* Group header — clickable to expand/collapse */}
            <button
              type="button"
              onClick={() => toggleGroup(category)}
              className="w-full px-4 py-2.5 bg-[hsl(var(--surface-subtle))] border-b border-[hsl(var(--border))] flex items-center justify-between hover:bg-[hsl(var(--surface))] transition-colors"
            >
              <div className="flex items-center gap-2">
                {isCollapsed
                  ? <ChevronRight className="h-4 w-4 text-[hsl(var(--content-subtle))] shrink-0" />
                  : <ChevronDown className="h-4 w-4 text-[hsl(var(--content-subtle))] shrink-0" />}
                <div className="text-left">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--content-subtle))]">{category}</h3>
                  <p className="text-xs text-[hsl(var(--content-subtle))]">{assignedCount}/{groupSkills.length} assigned · {expertCount} expert</p>
                </div>
              </div>
              {canEdit && !isCollapsed && (
                <div
                  className="flex items-center gap-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="text-xs text-[hsl(var(--content-subtle))]">Set all:</span>
                  <select
                    value={bulkLevel[category] ?? ''}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const val = e.target.value;
                      setBulkLevel((prev) => ({ ...prev, [category]: val }));
                      if (val) setAllForGroup(groupSkills, Number(val));
                    }}
                    className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-2 py-1 text-xs text-[hsl(var(--content))] focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="">—</option>
                    {SKILL_LEVELS.map((lbl, idx) => <option key={idx} value={idx}>{lbl}</option>)}
                  </select>
                </div>
              )}
            </button>
            {/* Skills list — hidden when collapsed */}
            {!isCollapsed && (
              <div className="divide-y divide-[hsl(var(--border))]">
                {groupSkills.map((s) => (
                  <div key={s.skillId} className="flex items-center justify-between px-4 py-2.5">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[hsl(var(--content))] truncate">{s.skillName}</p>
                      <p className="text-xs text-[hsl(var(--content-subtle))]">
                        {s.source}{s.inferred ? ' · AI-inferred' : ''}{s.confidence ? ' · ' + Math.round(s.confidence * 100) + '% conf' : ''}
                      </p>
                    </div>
                    {canEdit ? (
                      <select
                        value={s.level}
                        onChange={(e) => setLevel(s.skillId, Number(e.target.value))}
                        className={'rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-2 py-1 text-sm text-[hsl(var(--content))] focus:outline-none focus:ring-2 focus:ring-brand-500 shrink-0 ml-3 ' + (s.level > 0 ? LEVEL_COLORS[s.level] : '')}
                      >
                        {SKILL_LEVELS.map((lbl, idx) => <option key={idx} value={idx}>{idx} — {lbl}</option>)}
                      </select>
                    ) : (
                      <span className={'text-xs px-2 py-0.5 rounded font-medium shrink-0 ml-3 ' + (s.level > 0 ? LEVEL_COLORS[s.level] : LEVEL_COLORS[0])}>
                        {SKILL_LEVELS[s.level]}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

